import { useCallback, useMemo, useState } from 'react';


import {
  applyColumnPrefs,
  columnPrefKey,
  isPreferenceColumn,
  readColumnPrefs,
} from './column-prefs';
import type { ProjectColumnKey } from './column-types';

import { useProjectContext } from '@/features/projects/layout/ProjectPageLayout';

/**
 * Hidden-column set for a project task table.
 *
 * Inside a project page the choice is shared and persisted on the project
 * (`saveColumnPrefs`), so it survives reload and is the same for every member.
 * With no project in context (standalone render) it falls back to local state.
 */
export function useProjectColumnVisibility(defaultHidden: readonly ProjectColumnKey[]) {
  const { project, saveColumnPrefs } = useProjectContext();
  const persisted = !!project && !!saveColumnPrefs;
  const [local, setLocal] = useState<Set<ProjectColumnKey>>(() => new Set(defaultHidden));

  const storedColumns = project?.columnConfig?.columns;
  const persistedHidden = useMemo(
    () => applyColumnPrefs(defaultHidden, readColumnPrefs(storedColumns)),
    [defaultHidden, storedColumns],
  );
  const hidden = persisted ? persistedHidden : local;

  const toggle = useCallback(
    (key: ProjectColumnKey) => {
      if (!isPreferenceColumn(key)) return;
      if (persisted) {
        const shownNow = !persistedHidden.has(key);
        void saveColumnPrefs({ [columnPrefKey(key)]: !shownNow });
        return;
      }
      setLocal((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [persisted, persistedHidden, saveColumnPrefs],
  );

  return { hidden, toggle };
}
